import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import {
  Package, AlertTriangle, Truck, Store, IndianRupee, Banknote, Scale, Calculator,
  Boxes, ClipboardList, Users, Download, FileBarChart, PhoneCall,
} from 'lucide-react';
import { api } from '@/api/client';
import { Skeleton, ChartSkeleton } from '@/components/ui/Skeleton';

type OT = 'ALL' | 'B2C' | 'B2B';
type Fmt = 'num' | 'money' | 'pct' | undefined;
type Column = { key: string; label: string; align?: 'left' | 'right'; fmt?: Fmt };
type Series = { key: string; label: string; color?: string };
type Chart = { type: 'bar' | 'stacked' | 'pie' | 'line'; xKey: string; series: Series[] };
type Section = { title: string; columns: Column[]; rows: any[]; chart?: Chart };
type Kpi = { label: string; value: number; fmt?: Fmt; tone?: string };
type Report = { module: string; orderType: OT; range: { from: string; to: string }; kpis: Kpi[]; sections: Section[] };

const MODULES: { id: string; label: string; icon: React.ReactNode; usesType: boolean }[] = [
  { id: 'shipments', label: 'Shipments', icon: <Package size={15} />, usesType: true },
  { id: 'ndr', label: 'NDR', icon: <AlertTriangle size={15} />, usesType: true },
  // Call logs aren't split by B2C/B2B — a call is about a customer, not a shipment class.
  { id: 'ivr', label: 'IVR / Calls', icon: <PhoneCall size={15} />, usesType: false },
  { id: 'couriers', label: 'Couriers', icon: <Truck size={15} />, usesType: true },
  { id: 'merchants', label: 'Merchants', icon: <Store size={15} />, usesType: true },
  { id: 'finance', label: 'Finance', icon: <IndianRupee size={15} />, usesType: true },
  { id: 'remittance', label: 'Remittance / COD', icon: <Banknote size={15} />, usesType: true },
  { id: 'weight', label: 'Weight Disputes', icon: <Scale size={15} />, usesType: true },
  { id: 'freight', label: 'Freight', icon: <Calculator size={15} />, usesType: false },
  { id: 'wms', label: 'WMS / Inventory', icon: <Boxes size={15} />, usesType: false },
  { id: 'pickup', label: 'Pickups', icon: <ClipboardList size={15} />, usesType: false },
  { id: 'hr', label: 'HR', icon: <Users size={15} />, usesType: false },
];

const PALETTE = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899', '#6366f1', '#84cc16', '#f97316'];
const num = (n: any) => Number(n ?? 0).toLocaleString('en-IN');
const money = (n: any) => '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const fmtVal = (v: any, fmt: Fmt, align?: 'left' | 'right') =>
  fmt === 'money' ? money(v) : fmt === 'pct' ? `${Number(v ?? 0)}%` : (typeof v === 'number' && align === 'right') ? num(v) : (fmt === 'num' ? num(v) : v);
const toneClass: Record<string, string> = {
  emerald: 'text-emerald-700', rose: 'text-rose-700', blue: 'text-sky-700',
  violet: 'text-violet-700', amber: 'text-amber-700',
};

export function ReportsPage() {
  const [sp, setSp] = useSearchParams();
  const [module, setModule] = useState<string>(sp.get('module') || 'shipments');
  const [ot, setOt] = useState<OT>('ALL');
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));

  useEffect(() => { setSp(module === 'shipments' ? {} : { module }, { replace: true }); }, [module]); // eslint-disable-line react-hooks/exhaustive-deps

  const meta = MODULES.find((m) => m.id === module)!;
  const { data, isLoading, isError } = useQuery<Report>({
    queryKey: ['report', module, ot, from, to],
    queryFn: async () => (await api.get<Report>('/dashboard/reports', { params: { module, orderType: meta.usesType ? ot : 'ALL', from, to } })).data,
    staleTime: 30_000,
  });

  function exportAll() {
    if (!data) return;
    const esc = (v: any) => { const s = String(v ?? ''); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const L: string[] = [];
    L.push(`Report,${meta.label}`, `Scope,${meta.usesType ? ot : 'ALL'}`, `From,${from}`, `To,${to}`, '');
    // Summary counts (KPIs)
    L.push('Summary,Value');
    (data.kpis ?? []).forEach((k) => L.push(`${esc(k.label)},${esc(k.value)}`));
    // Each section table
    (data.sections ?? []).forEach((s) => {
      L.push('', esc(s.title));
      L.push(s.columns.map((c) => esc(c.label)).join(','));
      s.rows.forEach((r) => L.push(s.columns.map((c) => esc(r[c.key])).join(',')));
    });
    const blob = new Blob([L.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `report-${module}-${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileBarChart size={22} className="text-primary" /> Reports</h1>
          <p className="text-slate-500 text-sm mt-0.5">Module-by-module summary reports — tables &amp; charts across your logistics business.</p>
        </div>
        <button onClick={exportAll} disabled={!data} title="Download summary counts + all tables (CSV)"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50 shrink-0">
          <Download size={15} /> Export
        </button>
      </div>

      {/* Module selector */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {MODULES.map((m) => (
          <button key={m.id} onClick={() => setModule(m.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition ${module === m.id ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary'}`}>
            {m.icon} {m.label}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className={`flex items-center gap-1 bg-slate-100 rounded-lg p-1 ${meta.usesType ? '' : 'opacity-40 pointer-events-none'}`} title={meta.usesType ? '' : 'This report is not split by B2C/B2B'}>
          {(['ALL', 'B2C', 'B2B'] as OT[]).map((t) => (
            <button key={t} onClick={() => setOt(t)}
              className={`px-4 py-1.5 text-sm rounded-md transition ${ot === t ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
              {t === 'ALL' ? 'All' : t}
            </button>
          ))}
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-0.5">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-0.5">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div className="flex gap-1">
          {/* Each preset carries its OWN `to` offset — "Yesterday" is the one range that doesn't end
              today, so a single shared to=daysAgo(0) couldn't express it. Mirrors the dashboards. */}
          {[
            { l: 'Last 30 days', from: 29, to: 0 },
            { l: 'Last 7 days', from: 6, to: 0 },
            { l: 'Yesterday', from: 1, to: 1 },
            { l: 'Today', from: 0, to: 0 },
          ].map((p) => {
            const active = from === daysAgo(p.from) && to === daysAgo(p.to);
            return (
              <button key={p.l} onClick={() => { setFrom(daysAgo(p.from)); setTo(daysAgo(p.to)); }}
                className={`text-xs px-2 py-1.5 border rounded transition ${active ? 'bg-primary text-white border-primary' : 'text-slate-600 hover:bg-slate-50'}`}>{p.l}</button>
            );
          })}
        </div>
      </div>

      {isLoading ? (
        /* Mirrors the real report body below — a KPI strip, then the chart — so the page keeps its
           shape while the query runs instead of collapsing to a line of text and jumping back. */
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-7 w-24 mt-2" />
              </div>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <ChartSkeleton />
          </div>
        </>
      ) : isError ? (
        <div className="text-center py-20 text-rose-500">Couldn't load this report. Please try again.</div>
      ) : (
        <>
          {/* KPIs */}
          {(data?.kpis?.length ?? 0) > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-6">
              {data!.kpis.map((k) => (
                <div key={k.label} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-xs text-slate-500">{k.label}</div>
                  <div className={`text-2xl font-bold mt-1 ${k.tone ? (toneClass[k.tone] ?? 'text-slate-900') : 'text-slate-900'}`}>
                    {fmtVal(k.value, k.fmt, 'right')}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sections */}
          <div className="space-y-6">
            {(data?.sections ?? []).map((s) => <SectionBlock key={s.title} section={s} module={module} />)}
            {(data?.sections?.length ?? 0) === 0 && (
              <div className="text-center py-16 text-slate-400">No data available for this report.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SectionBlock({ section, module }: { section: Section; module: string }) {
  const { title, columns, rows, chart } = section;
  const exportCsv = () => {
    const header = columns.map((c) => c.label).join(',');
    const body = rows.map((r) => columns.map((c) => {
      const v = r[c.key];
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${module}-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        {rows.length > 0 && (
          <button onClick={exportCsv} className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded text-slate-500 hover:bg-slate-50">
            <Download size={12} /> CSV
          </button>
        )}
      </div>
      <div className={`grid grid-cols-1 ${chart ? 'xl:grid-cols-2' : ''} gap-3`}>
        <DataTable columns={columns} rows={rows} />
        {chart && <div className="bg-white border border-slate-200 rounded-xl p-3"><ChartBlock chart={chart} columns={columns} rows={rows} /></div>}
      </div>
    </div>
  );
}

function DataTable({ columns, rows }: { columns: Column[]; rows: any[] }) {
  const numericCols = columns.filter((c) => c.align === 'right');
  const totals: Record<string, number> = {};
  for (const c of numericCols) if (c.fmt !== 'pct') totals[c.key] = rows.reduce((s, r) => s + (Number(r[c.key]) || 0), 0);
  return (
    <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl max-h-[520px] overflow-y-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
          <tr>{columns.map((c) => <th key={c.key} className={`px-4 py-2.5 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-8 text-slate-400">No data</td></tr>
          ) : rows.map((r, ri) => (
            <tr key={ri} className="border-t border-slate-100">
              {columns.map((c) => (
                <td key={c.key} className={`px-4 py-2 ${c.align === 'right' ? 'text-right tabular-nums text-slate-600' : 'text-slate-700'} ${c.align !== 'right' ? 'truncate max-w-[260px]' : ''}`}>
                  {fmtVal(r[c.key], c.fmt, c.align)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && Object.keys(totals).length > 0 && (
          <tfoot className="sticky bottom-0">
            <tr className="border-t-2 bg-slate-50 font-semibold">
              {columns.map((c, i) => (
                <td key={c.key} className={`px-4 py-2 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                  {i === 0 ? 'Total' : (c.key in totals ? fmtVal(totals[c.key], c.fmt, 'right') : '')}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function ChartBlock({ chart, columns, rows }: { chart: Chart; columns: Column[]; rows: any[] }) {
  const data = rows.filter((r) => chart.series.some((s) => Number(r[s.key]) !== 0));
  if (!data.length) return <div className="h-[320px] flex items-center justify-center text-slate-400 text-sm">No data</div>;
  const seriesFmt = (key: string): Fmt => columns.find((c) => c.key === key)?.fmt;
  const tip = (v: any, name: any) => {
    const s = chart.series.find((x) => x.label === name || x.key === name);
    const fmt = s ? seriesFmt(s.key) : undefined;
    return fmt === 'money' ? money(v) : num(v);
  };
  const height = Math.max(320, Math.min(data.length * 30, 560));

  if (chart.type === 'pie') {
    const key = chart.series[0].key;
    const fmt = seriesFmt(key);
    return (
      <ResponsiveContainer width="100%" height={340}>
        <PieChart>
          <Pie data={data} dataKey={key} nameKey={chart.xKey} cx="50%" cy="50%" outerRadius={110} label={(e: any) => e[chart.xKey]}>
            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip formatter={(v: any) => fmt === 'money' ? money(v) : num(v)} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chart.type === 'line') {
    return (
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={data} margin={{ left: 4, right: 16, top: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={chart.xKey} tick={{ fontSize: 10 }} tickFormatter={(d) => String(d).slice(5)} />
          <YAxis tick={{ fontSize: 11 }} width={44} />
          <Tooltip formatter={tip} />
          <Legend />
          {chart.series.map((s, i) => <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color ?? PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />)}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // bar (single/grouped) or stacked — horizontal for readable long labels
  const single = chart.series.length === 1;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
        <XAxis type="number" tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey={chart.xKey} width={150} tick={{ fontSize: 10 }} />
        <Tooltip formatter={tip} />
        {!single && <Legend />}
        {chart.series.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.label} stackId={chart.type === 'stacked' ? 'a' : undefined}
            fill={s.color ?? PALETTE[i % PALETTE.length]} radius={chart.type === 'stacked' && i < chart.series.length - 1 ? undefined : [0, 4, 4, 0]}>
            {single && data.map((_, j) => <Cell key={j} fill={PALETTE[j % PALETTE.length]} />)}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
